import React, { useState, useRef, useEffect } from "react";

const FILTER_SKELETON_TABS = 4;

const SkeletonBox = ({ width = 140, height = 44, borderRadius = 22 }) => (
  <span
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      background:
        "linear-gradient(90deg,#eeeeee 25%,#f5f5f5 37%,#eeeeee 63%)",
      backgroundSize: "400% 100%",
      animation: "branchesFilterShimmer 1.4s ease infinite",
    }}
  />
);

function BranchesFilter({
  tabs = [],
  loading = false,
  activeColor = "#94C23C",
  inactiveColor = "#1414140A",
  activeBadgeBg = "#182A4F",
  onTabChange,
}) {
  const [activeId, setActiveId] = useState(tabs[0]?.id);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const activeTab = tabs.find((t) => t.id === activeId);

  const handleClick = (tab) => {
    setActiveId(tab.id);
    setDropdownOpen(false);
    onTabChange && onTabChange(tab);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  if (loading) {
    return (
      <div className="branches-filter">
        <style>{shimmerKeyframes}</style>
        <ul
          className="nav nav-tabs d-none d-md-flex"
          style={{ gap: "12px", border: "none", listStyle: "none", padding: 0 }}
        >
          {Array.from({ length: FILTER_SKELETON_TABS }).map((_, index) => (
            <li key={`filter-skeleton-${index}`} className="nav-item">
              <SkeletonBox />
            </li>
          ))}
        </ul>

        <div
          className="d-flex d-md-none"
          style={{ justifyContent: "center", marginBottom: "20px" }}
        >
          <SkeletonBox width={200} />
        </div>
      </div>
    );
  }

  return (
    <div className="branches-filter">
      {/* Desktop: tabs (hidden on mobile) */}
      <ul
        className="nav nav-tabs d-none d-md-flex"
        role="tablist"
        style={{
          gap: "12px",
          border: "none",
          listStyle: "none",
          padding: 0,
          justifyContent: "center",
          marginBottom: "24px",
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <li key={tab.id} className="nav-item" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleClick(tab)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  border: "none",
                  borderRadius: "22px",
                  padding: "10px 20px",
                  fontWeight: 600,
                  fontSize: "16px",
                  cursor: "pointer",
                  background: isActive ? activeColor : inactiveColor,
                  color: isActive ? "#fff" : "#686868",
                }}
              >
                {tab.label}
                <span
                  style={{
                    background: isActive ? activeBadgeBg : "#68686833",
                    color: isActive ? "#fff" : "#686868",
                    borderRadius: "20px",
                    padding: "2px 8px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {tab.count}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Mobile: dropdown (hidden on desktop) */}
      <div
        className="d-flex d-md-none"
        ref={dropdownRef}
        style={{
          position: "relative",
          width: "100%",
          justifyContent: "center",
          marginBottom: "20px",
        }}
      >
        <button
          type="button"
          onClick={() => setDropdownOpen((prev) => !prev)}
          style={{
            background: activeColor,
            color: "#fff",
            border: "none",
            borderRadius: "22px",
            padding: "10px 19px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            fontSize: "16px",
            cursor: "pointer",
            gap: "4px",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {activeTab?.label}
            <span
              style={{
                background: activeBadgeBg,
                color: "#fff",
                borderRadius: "20px",
                padding: "2px 8px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {activeTab?.count}
            </span>
          </span>

          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transition: "transform 0.2s",
              transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {dropdownOpen && (
          <ul
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              listStyle: "none",
              margin: 0,
              padding: "6px 0",
              zIndex: 999,
              boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
            }}
          >
            {tabs.map((tab) => {
              const isActive = tab.id === activeId;
              return (
                <li key={tab.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(tab)}
                    style={{
                      width: "100%",
                      background: isActive ? `${activeColor}18` : "transparent",
                      color: isActive ? activeColor : "#686868",
                      border: "none",
                      padding: "10px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: "15px",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span>{tab.label}</span>
                    <span
                      style={{
                        background: isActive ? activeBadgeBg : "#68686833",
                        color: isActive ? "#fff" : "#686868",
                        borderRadius: "20px",
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {tab.count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

const shimmerKeyframes = `@keyframes branchesFilterShimmer {0%{background-position:100% 50%}100%{background-position:0 50%}}`;

export default BranchesFilter;
